import { useState } from "react";
import { NodeToolbar, Position } from "@xyflow/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBook } from "@fortawesome/free-solid-svg-icons";
import {
  normalizeEvidence,
  bestEvidence,
  evidenceIcon,
  evidenceTooltip,
  evidenceLabel,
  EVIDENCE_ORDER,
} from "../ReactionTable/evidenceTypes.js";

// Group a node's evidence_sources by evidence type, dedupe publications within
// each group, and order the groups best-supported first.
function groupByEvidence(sources) {
  const groups = new Map(); // evidence -> Map(publication_id -> pub)
  for (const source of sources) {
    const evidence = normalizeEvidence(source.mechanism_evidence);
    if (!groups.has(evidence)) groups.set(evidence, new Map());
    const pubs = groups.get(evidence);
    for (const pub of source.publications || []) {
      if (pub.publication_id != null) pubs.set(pub.publication_id, pub);
    }
  }
  return [...EVIDENCE_ORDER]
    .reverse() // best -> worst
    .filter((evidence) => groups.has(evidence))
    .map((evidence) => ({
      evidence,
      publications: Array.from(groups.get(evidence).values()),
    }));
}

function publicationHref(pub) {
  if (pub.publication_doi) {
    return pub.publication_doi.startsWith("https://doi.org/")
      ? pub.publication_doi
      : `https://doi.org/${pub.publication_doi}`;
  }
  return pub.publication_link;
}

// Badge shown on a mechanism graph node: the icon of the best-supported evidence
// among all mechanisms involving this molecule. Clicking it expands a list of
// every source, grouped by evidence type (best first).
function MoleculeNodeEvidence({ sources }) {
  const [open, setOpen] = useState(false);

  if (!sources || sources.length === 0) return null;

  const best = bestEvidence(sources.map((s) => s.mechanism_evidence));
  const groups = groupByEvidence(sources);

  return (
    <div className="node-evidence nodrag nopan">
      <button
        type="button"
        className="node-evidence-button"
        title={`Best-supported mechanism involving this molecule: ${evidenceTooltip(best)}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((isOpen) => !isOpen);
        }}
      >
        <FontAwesomeIcon icon={evidenceIcon(best)} color="rgb(0,0,0,0.8)" />
      </button>
      <NodeToolbar
        isVisible={open}
        position={Position.Bottom}
        className="node-evidence-popover nodrag nopan"
        onClick={(e) => e.stopPropagation()}
      >
          {groups.map(({ evidence, publications }) => (
            <div key={evidence} className="node-evidence-group">
              <div className="node-evidence-group-title">
                <FontAwesomeIcon icon={evidenceIcon(evidence)} color="rgb(0,0,0,0.8)" />{" "}
                {evidenceLabel(evidence)}
              </div>
              {publications.length > 0 ? (
                <div className="node-evidence-pub-list">
                  {publications.map((pub) => (
                    <a
                      key={pub.publication_id}
                      href={publicationHref(pub)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open publication"
                    >
                      <FontAwesomeIcon icon={faBook} color="rgb(0,0,0,0.8)" />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="node-evidence-no-pub">No publication listed</div>
              )}
            </div>
          ))}
      </NodeToolbar>
    </div>
  );
}

export default MoleculeNodeEvidence;
